import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface CompanyStats {
  overview: {
    totalEmployees: number;
    activeEmployees: number;
    inactiveEmployees: number;
  };
  courseProgress: Array<{
    courseTitle: string;
    totalUsers: number;
    completedUsers: number;
    completionRate: number;
    averageScore: number;
  }>;
  recentEvaluations: Array<{
    _id: string;
    score: number;
    completedAt: string;
    user: {
      firstName: string;
      lastName: string;
    };
    course: {
      title: string;
    };
  }>;
  topEmployees: Array<{
    firstName: string;
    lastName: string;
    email: string;
    department: string;
    totalEvaluations: number;
    averageScore: number;
    totalScore: number;
  }>;
}

interface CompanyInfo {
  _id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  authorizedDomains: string[];
  subscription: {
    plan: string;
    endDate: string;
    maxUsers: number;
  };
  isActive: boolean;
}

const CompanyDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsResponse, companyResponse] = await Promise.all([
        api.getCompanyAdminStats(),
        api.getCompanyInfo()
      ]);

      setStats(statsResponse);
      setCompanyInfo(companyResponse.company);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'basic': return 'default';
      case 'professional': return 'primary';
      case 'enterprise': return 'secondary';
      default: return 'default';
    }
  };

  const getPlanLabel = (plan: string) => {
    switch (plan) {
      case 'basic': return 'Básico';
      case 'professional': return 'Profesional';
      case 'enterprise': return 'Empresarial';
      default: return plan;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                Dashboard de Empresa
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {companyInfo?.name}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={fetchDashboardData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Estadísticas generales */}
        <Box sx={{ 
          mb: 4,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 3
        }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.overview.totalEmployees || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Empleados
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.overview.activeEmployees || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Empleados Activos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <WarningIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.overview.inactiveEmployees || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Empleados Inactivos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <SchoolIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.courseProgress.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cursos Activos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Información de la empresa */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Información de la Empresa
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, 1fr)'
              },
              gap: 3
            }}>
              <Box>
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Plan de Suscripción
                  </Typography>
                  <Chip
                    label={getPlanLabel(companyInfo?.subscription.plan || '')}
                    color={getPlanColor(companyInfo?.subscription.plan || '') as any}
                    size="small"
                  />
                </Box>
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Límite de Usuarios
                  </Typography>
                  <Typography variant="body1">
                    {companyInfo?.subscription.maxUsers} usuarios
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Fecha de Expiración
                  </Typography>
                  <Typography variant="body1">
                    {companyInfo?.subscription.endDate ? formatDate(companyInfo.subscription.endDate) : 'N/A'}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Dominios Autorizados
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {companyInfo?.authorizedDomains.map((domain, index) => (
                      <Chip
                        key={index}
                        label={domain}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email de Contacto
                  </Typography>
                  <Typography variant="body1">
                    {companyInfo?.email}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Tabs para diferentes secciones */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Progreso de Cursos" />
              <Tab label="Top Empleados" />
              <Tab label="Evaluaciones Recientes" />
            </Tabs>
          </Box>

          <CardContent>
            {/* Tab 1: Progreso de Cursos */}
            {activeTab === 0 && (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Curso</TableCell>
                      <TableCell align="center">Usuarios</TableCell>
                      <TableCell align="center">Completados</TableCell>
                      <TableCell align="center">Progreso</TableCell>
                      <TableCell align="center">Puntuación Promedio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats?.courseProgress.map((course, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {course.courseTitle}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Badge badgeContent={course.totalUsers} color="primary">
                            <PersonIcon />
                          </Badge>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {course.completedUsers} / {course.totalUsers}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            <LinearProgress
                              variant="determinate"
                              value={course.completionRate}
                              sx={{ width: 100, height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="body2">
                              {course.completionRate.toFixed(1)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${course.averageScore.toFixed(1)}%`}
                            color={getScoreColor(course.averageScore) as any}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 2: Top Empleados */}
            {activeTab === 1 && (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Empleado</TableCell>
                      <TableCell>Departamento</TableCell>
                      <TableCell align="center">Evaluaciones</TableCell>
                      <TableCell align="center">Puntuación Promedio</TableCell>
                      <TableCell align="center">Puntuación Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats?.topEmployees.map((employee, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {employee.firstName.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {employee.firstName} {employee.lastName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {employee.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {employee.department || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {employee.totalEvaluations}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${employee.averageScore.toFixed(1)}%`}
                            color={getScoreColor(employee.averageScore) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">
                            {employee.totalScore}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 3: Evaluaciones Recientes */}
            {activeTab === 2 && (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Empleado</TableCell>
                      <TableCell>Curso</TableCell>
                      <TableCell align="center">Puntuación</TableCell>
                      <TableCell align="center">Fecha</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats?.recentEvaluations.map((evaluation) => (
                      <TableRow key={evaluation._id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {evaluation.user.firstName.charAt(0)}
                            </Avatar>
                            <Typography variant="subtitle2">
                              {evaluation.user.firstName} {evaluation.user.lastName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {evaluation.course.title}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${evaluation.score}%`}
                            color={getScoreColor(evaluation.score) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {formatDate(evaluation.completedAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default CompanyDashboard;
